import { useHead } from '@vueuse/head'
import { Post } from '@/models/Cms'
import { FrameControl } from '@/models/FrameControl'

export function setPageMetaTags(
  title: string,
  description: string,
  imageUrl: string | null,
  url: string,
  jsonLd?: object,
  options?: {
    keywords?: string,
    author?: string,
    siteName?: string,
    locale?: string,
    themeColor?: string,
    twitterSite?: string
  }
): void {
  const meta = [
    { name: 'description', content: description },
    ...(options?.keywords ? [{ name: 'keywords', content: options.keywords }] : []),
    ...(options?.author ? [{ name: 'author', content: options.author }] : []),
    { name: 'robots', content: 'index,follow' },
    { property: 'og:title', content: title },
    { property: 'og:description', content: description },
    { property: 'og:type', content: 'website' },
    { property: 'og:url', content: url },
    ...(imageUrl ? [{ property: 'og:image', content: imageUrl }] : []),
    ...(options?.siteName ? [{ property: 'og:site_name', content: options.siteName }] : []),
    ...(options?.locale ? [{ property: 'og:locale', content: options.locale }] : []),
    { name: 'twitter:card', content: 'summary_large_image' },
    { name: 'twitter:title', content: title },
    { name: 'twitter:description', content: description },
    ...(imageUrl ? [{ name: 'twitter:image', content: imageUrl }] : []),
    ...(options?.twitterSite ? [{ name: 'twitter:site', content: options.twitterSite }] : []),
  ];
  useHead({
    title,
    meta: options?.themeColor ? [{ name: 'theme-color', content: options.themeColor }, ...meta] : meta,
    link: [
      { rel: 'canonical', href: url }
    ],
    ...(jsonLd ? {
      script: [
        {
          type: 'application/ld+json',
          children: JSON.stringify(jsonLd)
        }
      ]
    } : {})
  });
}

export function setPostMetaTags(post: Post, appConfigs: FrameControl, canonicalUrl?: string, options?: {
  keywords?: string,
  locale?: string,
  twitterSite?: string
}): void {
  const title = post.attributes.title;
  const rawExcerpt = post.attributes.description || post.attributes.content || '';
  const excerpt = rawExcerpt.length > 160 ? rawExcerpt.substring(0, 157) + '...' : rawExcerpt;
  const imageUrl = post.attributes.thumbnail?.data?.attributes?.formats?.large?.url ||
                   post.attributes.thumbnail?.data?.attributes?.url || '';
  const authorName = post.attributes.author?.data?.attributes?.name || '';
  const category = post.attributes.category?.data?.attributes?.name || '';
  const publishedAt = post.attributes.publishedAt || post.attributes.createdAt || new Date().toISOString();
  const postSlug = post.attributes.slug || '';
  
  // Construct canonical URL properly
  let url = canonicalUrl;
  if (!url) {
    // Determine the production domain from the CMS base URL
    let productionDomain = '';
    if (appConfigs?.cms?.endpoints?.base) {
      const cmsBaseUrl = appConfigs.cms.endpoints.base;
      productionDomain = cmsBaseUrl.replace('https://blog.', '').replace('http://blog.', '');
    }
    
    if (typeof window !== 'undefined') {
      // Use the current pathname but with the production domain
      const currentPath = window.location.pathname;
      const currentSearch = window.location.search.replace('&live=true', '').replace('?live=true', '');
      const currentHash = window.location.hash;
      
      if (productionDomain) {
        url = `https://${productionDomain}${currentPath}${currentSearch}${currentHash}`;
      } else {
        // If we can't determine the domain, fall back to window.location.href
        // but remove the live=true parameter
        url = window.location.href.replace('&live=true', '').replace('?live=true', '');
      }
    } else {
      // During SSR/prerendering, construct URL from post slug
      if (productionDomain && postSlug) {
        url = `https://${productionDomain}/post/${encodeURIComponent(postSlug)}`;
      } else if (postSlug) {
        // Fallback: construct relative URL if domain not available
        url = `/post/${encodeURIComponent(postSlug)}`;
      } else {
        // Last resort: empty string (shouldn't happen if post has slug)
        url = '';
      }
    }
  }
  const siteName = appConfigs.general.name;
  const themeColor = appConfigs.general.display?.themeColor;
  const locale = options?.locale || 'en_US';
  const twitterSite = options?.twitterSite;
  const meta = [
    { name: 'description', content: excerpt },
    ...(options?.keywords ? [{ name: 'keywords', content: options.keywords }] : []),
    { name: 'author', content: authorName },
    { name: 'robots', content: 'index,follow' },
    { property: 'og:title', content: title },
    { property: 'og:description', content: excerpt },
    { property: 'og:image', content: imageUrl },
    { property: 'og:type', content: 'article' },
    { property: 'og:url', content: url },
    { property: 'og:site_name', content: siteName },
    { property: 'og:locale', content: locale },
    { property: 'article:published_time', content: publishedAt },
    { property: 'article:author', content: authorName },
    { property: 'article:tag', content: category },
    { name: 'twitter:card', content: 'summary_large_image' },
    { name: 'twitter:title', content: title },
    { name: 'twitter:description', content: excerpt },
    { name: 'twitter:image', content: imageUrl },
    { name: 'twitter:image:alt', content: title },
    ...(twitterSite ? [{ name: 'twitter:site', content: twitterSite }] : []),
  ];
  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "BlogPosting",
    "headline": title,
    "description": excerpt,
    "author": {
      "@type": "Person",
      "name": authorName
    },
    "datePublished": publishedAt,
    "url": url,
    ...(imageUrl && { "image": imageUrl }),
    "publisher": {
      "@type": "Organization",
      "name": siteName,
    }
  };
  useHead({
    title: `${title}`,
    meta: themeColor ? [{ name: 'theme-color', content: themeColor }, ...meta] : meta,
    link: [
      { rel: 'canonical', href: url }
    ],
    script: [
      {
        type: 'application/ld+json',
        children: JSON.stringify(jsonLd)
      }
    ]
  });
}

export function emitPrerenderEvent(): void {
    // 🔔 Signal pre-render is ready
    document.dispatchEvent(new Event('render-event'));
    // eslint-disable-next-line @typescript-eslint/no-explicit-any, @typescript-eslint/ban-ts-comment
    // @ts-ignore
    window.prerenderReady = true; 
}

export function detectPrerenderedPage(): boolean {
  if (typeof window !== 'undefined') {
    const urlParams = new URLSearchParams(window.location.search);
    // If live=true is present, always return false (not prerendered) to force live Vue app
    if (urlParams.has('live')) {
      return false;
    }
    if (!urlParams.has('invalidate_cache')) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any, @typescript-eslint/ban-ts-comment
      return (window as any).__PRERENDERING__ === true;
    }
  }
  return false;
}
